import * as React from 'react';
import CategoryNavigation from './components/category-navigation';
import ProductListFilters from './components/product-list-filters';
import ProductList from './components/product-list';
import ProductListPagination from './components/product-list-filters/product-list-pagination';
import ProductListHeadMetadata from './components/product-list-head-metadata';
import ProductListPageSummary from './components/product-list-page-summary';
import SeoSection from './components/seo-section';
import { ICusProductListViewProps } from './cus-product-list.data';
import useProductList from './hooks/useProductList';
import { buildPlpMetadata } from './hooks/buildPlpMetadata';
import ServerSideRedirect from '../../components/server-side-redirect/server-side-redirect';
import { useSyncGenderCookie } from '../../shared/services/gender-cookie-service/hooks/use-sync-gender-cookie';
import { parseFilterOptions } from '../../utils/productListFilters';

export default (props: ICusProductListViewProps) => {
    const {
        plpRef,
        pageTitle,
        totalItems,
        productCountText,
        isEmptySearch,
        isLoading,
        isSearchPage,
        itemsPerPage,
        categoryRecordId,
        currentSort,
        handleSortChange,
        mobileColumns,
        setMobileColumns,
        search,
        applyFiltersAndSort,
        hasNoResults,
        activeProducts,
        totalPages,
        currentPage,
        handlePageChange
    } = useProductList(props);

    const [initialSearchGender] = React.useState(() =>
        isSearchPage ? parseFilterOptions(key => props.context.request.query?.[key])?.gender : undefined
    );
    const currentCategory = props.data.categoryNavigation.result?.currentCategory ?? null;
    const parentCategory = props.data.categoryNavigation.result?.parentCategory ?? null;
    useSyncGenderCookie(isSearchPage ? initialSearchGender : currentCategory?.gender, props.context);

    const { pageSummary, headMetadata } = buildPlpMetadata({
        context: props.context,
        activeProducts,
        currentCategory,
        parentCategory,
        currentPage,
        isCategoryPage: !isSearchPage,
        pageTitle,
        homeLabel: props.resources.homeBreadcrumbLabel
    });

    return (
        <div className='bs-product-list' ref={plpRef} data-testid='plp-root'>
            <h1 className='bs-product-list__title' data-testid='plp-title'>
                {pageTitle}
                {totalItems > 0 && <span className='bs-product-list__product-count'>{productCountText}</span>}
            </h1>
            <div className='bs-product-list__body'>
                {!isSearchPage && categoryRecordId && (
                    <section>
                        <CategoryNavigation
                            response={props.data.categoryNavigation}
                            subCategoriesAriaLabel={props.resources.subCategoriesAriaLabel}
                        />
                    </section>
                )}
                {isEmptySearch && <p className='bs-product-list__empty-search'>{props.resources.emptySearchQueryText}</p>}
                {!isEmptySearch && (
                    <ProductListFilters
                        props={props}
                        isSearchPage={isSearchPage}
                        currentSort={currentSort}
                        onSortChange={handleSortChange}
                        mobileColumns={mobileColumns}
                        onMobileColumnsChange={setMobileColumns}
                        onSearch={search}
                        onApply={applyFiltersAndSort}
                        isLoading={isLoading}
                    />
                )}
                {!isLoading && hasNoResults && <p className='bs-product-list__empty-search'>{props.resources.resultSearchNotFoundText}</p>}
                <ProductList
                    props={props}
                    categoryProducts={activeProducts}
                    mobileColumns={mobileColumns}
                    isLoading={isLoading}
                    itemsPerPage={itemsPerPage}
                />
                {!isLoading && totalPages > 1 && (
                    <ProductListPagination
                        props={props}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={totalItems}
                        itemsPerPage={itemsPerPage}
                        onPageChange={handlePageChange}
                    />
                )}
            </div>
            <SeoSection props={props} />
            <ProductListPageSummary {...pageSummary} />
            <ProductListHeadMetadata {...headMetadata} />
            {pageSummary.redirectUrl && <ServerSideRedirect redirectUrl={pageSummary.redirectUrl} />}
        </div>
    );
};
